import { Popover, Radio } from "antd";
import React, { ReactElement, useState } from "react";
import settingIcon from "./mb-cog.png";
import styles from "./AiMedicalRecordSetting.module.css";

export type AiMedicalRecordCategory = 10 | 11 | 13 | 14;
export type AiMedicalRecordType = "general" | "tcm";

interface AiMedicalRecordSettingProps {
  recordType: AiMedicalRecordType;
  value: AiMedicalRecordCategory;
  onChange: (value: AiMedicalRecordCategory) => void;
}

const generalOptions: Array<{
  label: string;
  value: AiMedicalRecordCategory;
}> = [
  { label: "西医病历", value: 10 },
  { label: "中医病历", value: 11 },
  { label: "针灸病历", value: 13 },
  { label: "推拿病历", value: 14 },
];

const tcmOptions: Array<{
  label: string;
  value: AiMedicalRecordCategory;
}> = [
  { label: "中医病历", value: 11 },
  { label: "针灸病历", value: 13 },
  { label: "推拿病历", value: 14 },
];

const storageKeys: Record<AiMedicalRecordType, string> = {
  general: "aiMedicalRecordCategory_general",
  tcm: "aiMedicalRecordCategory_tcm",
};

export const getStoredAiMedicalRecordCategory = (
  recordType: AiMedicalRecordType
): AiMedicalRecordCategory | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const category = Number(localStorage.getItem(storageKeys[recordType]));
  const options = recordType === "general" ? generalOptions : tcmOptions;
  return options.some((option) => option.value === category)
    ? (category as AiMedicalRecordCategory)
    : undefined;
};

const saveAiMedicalRecordCategory = (
  recordType: AiMedicalRecordType,
  category: AiMedicalRecordCategory
) => {
  localStorage.setItem(storageKeys[recordType], String(category));
};

export const getTcmDefaultAiCategory = (
  departmentName?: string
): AiMedicalRecordCategory => {
  if (departmentName?.includes("推拿")) {
    return 14;
  }
  if (departmentName?.includes("针灸")) {
    return 13;
  }
  return 11;
};

export const getGeneralDefaultAiCategory = (
  departmentName?: string
): AiMedicalRecordCategory => {
  if (departmentName?.includes("推拿")) {
    return 14;
  }
  if (departmentName?.includes("针灸")) {
    return 13;
  }
  return 10;
};

export const AiMedicalRecordSetting = ({
  recordType,
  value,
  onChange,
}: AiMedicalRecordSettingProps): ReactElement => {
  const [visible, setVisible] = useState(false);
  const options = recordType === "general" ? generalOptions : tcmOptions;

  const content = (
    <div
      className={styles.content}
      onClick={(event) => event.stopPropagation()}
    >
      <div className={styles.title}>病历类型：</div>
      <Radio.Group
        value={value}
        onChange={(event) => {
          const category = event.target.value as AiMedicalRecordCategory;
          saveAiMedicalRecordCategory(recordType, category);
          onChange(category);
        }}
      >
        {options.map((option) => (
          <Radio key={option.value} value={option.value}>
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
    </div>
  );

  return (
    <Popover
      content={content}
      overlayClassName={styles.popover}
      placement="bottomLeft"
      trigger="click"
      visible={visible}
      onVisibleChange={setVisible}
    >
      <span
        className={styles.settingButton}
        role="button"
        tabIndex={0}
        aria-label="设置AI病历类型"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            setVisible((current) => !current);
          }
        }}
      >
        <img src={settingIcon} alt="设置" />
      </span>
    </Popover>
  );
};
